/**
 * CSS avançado de elemento, seção e coluna.
 * Declarações sem chave continuam no style em linha.
 * Regras com seletor vão para a folha <style>, e a classe do seletor raiz entra no class da div.
 */

function sanitizeAdvancedCss(css) {
  return String(css || '').replace(/expression\s*\(|javascript\s*:|<\/style/gi, '').trim();
}

function pushClassName(names, seen, name) {
  if (!name || seen.has(name)) return;
  if (!/^-?[_a-zA-Z][_a-zA-Z0-9-]*$/.test(name)) return;
  seen.add(name);
  names.push(name);
}

/** Classes do composto inicial de cada seletor (ignora descendentes e prelúdios @media/@keyframes). */
export function hostClassNamesFromCss(css) {
  const names = [];
  const seen = new Set();
  const stripped = String(css || '').replace(/\/\*[\s\S]*?\*\//g, '');
  let depth = 0;
  let buf = '';
  for (let i = 0; i < stripped.length; i += 1) {
    const ch = stripped[i];
    if (ch === '{') {
      const prelude = buf.trim();
      buf = '';
      if (prelude && !prelude.startsWith('@')) {
        prelude.split(',').forEach((part) => {
          const first = (part.trim().split(/[\s>+~]/)[0] || '');
          const re = /\.(-?[_a-zA-Z][_a-zA-Z0-9-]*)/g;
          let match = re.exec(first);
          while (match) {
            pushClassName(names, seen, match[1]);
            match = re.exec(first);
          }
        });
      }
      depth += 1;
    } else if (ch === '}') {
      depth = Math.max(0, depth - 1);
      buf = '';
    } else {
      buf += ch;
    }
  }
  return names;
}

/**
 * @returns {{ inline: string, stylesheet: string, classNames: string[] }}
 */
export function partitionAdvancedCss(css) {
  const raw = sanitizeAdvancedCss(css);
  if (!raw) return { inline: '', stylesheet: '', classNames: [] };
  if (!raw.includes('{')) return { inline: raw, stylesheet: '', classNames: [] };
  return {
    inline: '',
    stylesheet: raw,
    classNames: hostClassNamesFromCss(raw),
  };
}

/** Junta nomes de classe já sanitizados, sem duplicar. */
export function mergeClassNames(...lists) {
  const out = [];
  const seen = new Set();
  lists.flat().forEach((raw) => {
    String(raw || '').split(/\s+/).forEach((name) => {
      const clean = name.replace(/[^a-zA-Z0-9_-]/g, '');
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      out.push(clean);
    });
  });
  return out.join(' ');
}

/** Empilha a regra na folha. Declarações simples ganham o seletor do elemento. */
export function appendAdvancedCssRule(rules, css, selector) {
  const part = partitionAdvancedCss(css);
  if (part.stylesheet) rules.push(part.stylesheet);
  else if (part.inline && selector) rules.push(`${selector} { ${part.inline} }`);
  return part;
}

/**
 * Campo "Custom css class".
 * Nome solto continua no class. Regra completa (com chaves) vai para a folha e só a classe fica no elemento.
 */
export function readCustomClassField(value) {
  const raw = String(value || '').trim();
  if (!raw || !raw.includes('{')) {
    return { classNames: mergeClassNames(raw), stylesheet: '' };
  }
  const part = partitionAdvancedCss(raw);
  return { classNames: part.classNames.join(' '), stylesheet: part.stylesheet };
}

/** Junta o CSS avançado e o campo de classe customizada. */
export function elementAdvancedCss(advancedCss, customClass) {
  const custom = readCustomClassField(customClass);
  const adv = partitionAdvancedCss(advancedCss);
  return {
    inline: adv.inline,
    classNames: mergeClassNames(custom.classNames, adv.classNames),
    stylesheet: [custom.stylesheet, adv.stylesheet].filter(Boolean).join('\n'),
  };
}

/** Folha de estilo do elemento: regra da classe customizada + CSS avançado. */
export function appendElementAdvancedCss(rules, advancedCss, customClass, selector) {
  const custom = readCustomClassField(customClass);
  if (custom.stylesheet) rules.push(custom.stylesheet);
  return appendAdvancedCssRule(rules, advancedCss, selector);
}
